import React from 'react';

// Defendly AI Logo Component - Stylized "D" icon with BSEC branding
export const DefendlyLogo = ({ size = 'md', showText = true, showBSEC = false, className = '' }) => {
  const sizes = {
    xs: { icon: 20, text: 'text-xs', badge: 'text-[8px]' },
    sm: { icon: 28, text: 'text-sm', badge: 'text-[10px]' },
    md: { icon: 36, text: 'text-base', badge: 'text-xs' },
    lg: { icon: 48, text: 'text-xl', badge: 'text-sm' },
    xl: { icon: 64, text: 'text-2xl', badge: 'text-base' },
  };

  const s = sizes[size] || sizes.md;

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Stylized "D" Icon */}
      <div className="relative">
        <svg
          width={s.icon}
          height={s.icon}
          viewBox="0 0 48 48"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="drop-shadow-[0_0_8px_rgba(0,191,255,0.5)]"
        >
          {/* Glow filter */}
          <defs>
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#00BFFF" />
              <stop offset="100%" stopColor="#03FFFF" />
            </linearGradient>
          </defs>
          
          {/* Outer "D" shape - semicircle */}
          <path
            d="M12 8 L12 40 L24 40 C35 40 42 32 42 24 C42 16 35 8 24 8 L12 8"
            stroke="url(#logoGradient)"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            filter="url(#glow)"
          />
          
          {/* Inner arc - creates the layered effect */}
          <path
            d="M18 16 C26 16 32 20 32 24 C32 28 26 32 18 32"
            stroke="white"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
            filter="url(#glow)"
          />
          
          {/* Center accent dot */}
          <circle
            cx="22"
            cy="24"
            r="2"
            fill="#03FFFF"
            filter="url(#glow)"
          />
        </svg>
      </div>

      {/* Text branding */}
      {showText && (
        <div className="flex items-baseline gap-2">
          <span className={`font-inter font-semibold ${s.text} text-white tracking-tight`}>
            Defendly
            <span className="text-slate-400 ml-0.5">AI</span>
          </span>
          
          {showBSEC && (
            <span className={`font-inter font-bold ${s.badge} text-[#03FFFF] tracking-wide`}>
              BSEC
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// Compact logo for sidebar/mobile
export const DefendlyLogoCompact = ({ size = 32, className = '' }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 48 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={`drop-shadow-[0_0_6px_rgba(0,191,255,0.4)] ${className}`}
  >
    <defs>
      <filter id="glowCompact" x="-50%" y="-50%" width="200%" height="200%">
        <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
        <feMerge>
          <feMergeNode in="coloredBlur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
      <linearGradient id="logoGradientCompact" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#00BFFF" />
        <stop offset="100%" stopColor="#03FFFF" />
      </linearGradient>
    </defs>
    
    {/* Outer "D" shape */}
    <path
      d="M12 8 L12 40 L24 40 C35 40 42 32 42 24 C42 16 35 8 24 8 L12 8"
      stroke="url(#logoGradientCompact)"
      strokeWidth="3"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
      filter="url(#glowCompact)"
    />
    
    {/* Inner arc */}
    <path
      d="M18 16 C26 16 32 20 32 24 C32 28 26 32 18 32"
      stroke="white"
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
      filter="url(#glowCompact)"
    />
    
    {/* Center dot */}
    <circle
      cx="22"
      cy="24"
      r="2"
      fill="#03FFFF"
      filter="url(#glowCompact)"
    />
  </svg>
);

// BSEC Badge Component
export const BSECBadge = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'px-1.5 py-0.5 text-[9px]',
    md: 'px-2 py-1 text-[10px]',
    lg: 'px-3 py-1.5 text-xs',
  };

  return (
    <span className={`
      inline-flex items-center font-inter font-bold tracking-widest
      bg-[#03FFFF]/10 text-[#03FFFF] border border-[#03FFFF]/30
      rounded-md
      ${sizes[size] || sizes.md}
      ${className}
    `}>
      BSEC
    </span>
  );
};

export default DefendlyLogo;
